'use client';

import { useEffect, useState, useCallback } from 'react';
import { createDevLogger } from './logger';

export interface WebVital {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
}

export interface PerformanceTrackingOptions {
  /** Function to report web vitals metrics */
  onWebVital?: (metric: WebVital) => void;
  /** Whether to enable performance tracking */
  enabled?: boolean;
  /** Whether to log metrics to console in development */
  logToConsole?: boolean;
}

/**
 * Framework-agnostic performance metric reporter
 * Creates a stable callback function for reporting web vitals
 */
export function usePerformanceReporter(options: PerformanceTrackingOptions = {}) {
  const { onWebVital, enabled = true } = options;
  const [mounted, setMounted] = useState(false);
  const [reportedMetrics] = useState(() => new Set<string>());
  const logger = createDevLogger({
    namespace: 'Web Vital',
    includeTimestamp: false,
  });

  useEffect(() => {
    setMounted(true);
  }, []);

  // Create a stable callback function that can be used in other hooks
  const reportMetric = useCallback(
    (metric: WebVital) => {
      if (!mounted || !enabled) return;

      // Deduplicate metrics using composite key (name + value)
      // In React StrictMode (dev), hooks are called twice with different IDs but same values
      // Round value to prevent floating point precision issues
      const roundedValue = Math.round(metric.value * 100) / 100;
      const metricKey = `${metric.name}:${roundedValue}`;

      if (reportedMetrics.has(metricKey)) {
        return;
      }
      reportedMetrics.add(metricKey);

      logger.info(`metric: `, metric);

      // Call the injected reporting function
      if (onWebVital) {
        onWebVital(metric);
      }
    },
    [mounted, enabled, onWebVital, logger, reportedMetrics]
  );

  return { reportMetric, isMounted: mounted, isEnabled: enabled };
}

/**
 * Simple utility function to report web vitals without hooks
 * Can be used in any context
 */
export function createWebVitalReporter(options: PerformanceTrackingOptions = {}) {
  const { onWebVital, enabled = true } = options;
  const logger = createDevLogger({
    namespace: 'Web Vital',
    includeTimestamp: false,
  });

  // Track reported metrics to prevent duplicates (caused by React StrictMode)
  // Use composite key (name + value) since Next.js generates different IDs for duplicates
  const reportedMetrics = new Set<string>();

  return (metric: WebVital) => {
    if (!enabled) return;

    // Deduplicate metrics using composite key (name + value)
    // In React StrictMode (dev), hooks are called twice with different IDs but same values
    // Round value to prevent floating point precision issues
    const roundedValue = Math.round(metric.value * 100) / 100;
    const metricKey = `${metric.name}:${roundedValue}`;

    if (reportedMetrics.has(metricKey)) {
      return;
    }
    reportedMetrics.add(metricKey);

    logger.info(`metric: `, metric);

    // Call the injected reporting function
    if (onWebVital) {
      onWebVital(metric);
    }
  };
}

/**
 * Framework-agnostic Performance component placeholder
 */
function Performance() {
  console.warn(
    'Performance component from @marketing-web/utils is now framework-agnostic. ' +
      'Please create app-specific Performance components.'
  );
  return null;
}

export { Performance };
